import React, { useState, useRef, useEffect } from "react";
import { DateInput } from "../ui/DateInput";
import { getPatientAge } from "../../utils/age";
import { SpecializationTagInput } from "../ui/SpecializationTagInput";
import { useAddPatient } from "../../contexts/AddPatientContext";
import { toast } from "react-toastify";
import { logger } from "../../utils/logger";
import { validatePatientCreate, getFirstValidationError, type PatientCreateFormInput, type PatientDbRecord } from "../../schemas/patient.schema";
import { PatientAPI, PatientApiError } from "../../services/patient.service";
import { TagsAPI } from "../../services/tags.service";
import { putAndQueue } from "../../db/operations";
import type { PatientRecord } from "../../db/db.types";
import { db } from "../../db/schema";
import { actionErrorMessage, isBackendUnreachable, isPermissionDenied } from "../../utils/network";
import { INDIAN_STATES } from "../../constants/indianStates";

const COUNTRY_CODES = [
  { dial: "+91",  name: "India",          flag: "🇮🇳" },
  { dial: "+1",   name: "United States",  flag: "🇺🇸" },
  { dial: "+1",   name: "Canada",         flag: "🇨🇦" },
  { dial: "+44",  name: "United Kingdom", flag: "🇬🇧" },
  { dial: "+971", name: "UAE",            flag: "🇦🇪" },
  { dial: "+65",  name: "Singapore",      flag: "🇸🇬" },
  { dial: "+61",  name: "Australia",      flag: "🇦🇺" },
  { dial: "+49",  name: "Germany",        flag: "🇩🇪" },
  { dial: "+33",  name: "France",         flag: "🇫🇷" },
  { dial: "+81",  name: "Japan",          flag: "🇯🇵" },
  { dial: "+86",  name: "China",          flag: "🇨🇳" },
  { dial: "+92",  name: "Pakistan",       flag: "🇵🇰" },
  { dial: "+880", name: "Bangladesh",     flag: "🇧🇩" },
  { dial: "+94",  name: "Sri Lanka",      flag: "🇱🇰" },
  { dial: "+977", name: "Nepal",          flag: "🇳🇵" },
];

const DialSelect: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="h-11 flex items-center gap-1 pl-3 pr-2 text-xs font-semibold text-slate-700 bg-slate-50 border-r border-slate-200 whitespace-nowrap hover:bg-slate-100 transition-colors"
      >
        <span>{COUNTRY_CODES.find(c => c.dial === value)?.flag ?? ""}</span>
        <span>{value}</span>
        <svg className="w-3 h-3 text-slate-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[160px] max-h-56 overflow-y-auto">
          {COUNTRY_CODES.map((c, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { onChange(c.dial); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 transition-colors flex items-center justify-between gap-3 ${c.dial === value ? "text-cyan-600 font-semibold" : "text-slate-700"}`}
            >
              <span>{c.flag} {c.name}</span>
              <span className="font-mono text-slate-400">{c.dial}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface AddPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Receives the server-created patient; called with no argument when the
   *  patient was only saved offline and doesn't exist on the server yet. */
  onSuccess?: (patient?: PatientDbRecord) => void;
}

const initialFormData: PatientCreateFormInput = {
  first_name: "",
  middle_name: "",
  last_name: "",
  date_of_birth: "",
  age_years: "",
  gender: "" as "Male" | "Female" | "Other",
  contact_number: "",
  email: "",
  aadhar_number: "",
  alternate_contact_number: "",
  abha_id: "",
  house_no: "",
  building: "",
  street: "",
  locality: "",
  landmark: "",
  city: "",
  state: "",
  pincode: "",
  tags: [],
};

export const AddPatientModal: React.FC<AddPatientModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { prefill } = useAddPatient();

  const buildInitial = (): PatientCreateFormInput => {
    if (!prefill) return { ...initialFormData };
    const nameParts = (prefill.name ?? "").trim().split(/\s+/);
    const first_name = nameParts[0] ?? "";
    const last_name = nameParts.slice(1).join(" ");
    const genderMap: Record<string, string> = { M: "Male", F: "Female", male: "Male", female: "Female" };
    return {
      ...initialFormData,
      first_name,
      last_name,
      date_of_birth: prefill.dob ?? "",
      gender: (prefill.gender && genderMap[prefill.gender]) ?? prefill.gender ?? "",
      abha_id: prefill.abha_id ?? "",
    };
  };

  const [formData, setFormData] = useState<PatientCreateFormInput>(buildInitial);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Neonatal fields - one-time inputs at registration only, kept as separate
  // state (not merged into the flat PatientCreateFormInput, same as every
  // other nested sub-profile in this form) since they're only relevant for
  // pediatric-age patients and parse to numbers/booleans rather than strings.
  const initialPediatricData = {
    gestational_age_weeks: "",
    gestational_age_days: "",
    birth_weight_grams: "",
    is_preterm: false,
    multiple_birth: false,
    is_down_syndrome: false,
  };
  const [pediatricData, setPediatricData] = useState(initialPediatricData);
  const [pediatricError, setPediatricError] = useState<string | null>(null);

  const isPediatricAge = (() => {
    if (!formData.date_of_birth) return false;
    const dob = new Date(formData.date_of_birth);
    if (isNaN(dob.getTime())) return false;
    const ageMs = Date.now() - dob.getTime();
    return ageMs / (365.25 * 24 * 60 * 60 * 1000) < 5;
  })();

  const handlePediatricChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setPediatricData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (name === "is_preterm" || name === "gestational_age_weeks") setPediatricError(null);
  };

  // Safety net: this component is always mounted at app root (never truly unmounts),
  // so reset isSubmitting if the modal is closed while a submit is somehow still in flight.
  useEffect(() => {
    if (!isOpen) setIsSubmitting(false);
  }, [isOpen]);

  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  useEffect(() => {
    if (!isOpen) return;
    TagsAPI.list()
      .then((tags) => setTagSuggestions(tags.map((t) => t.name)))
      .catch(() => {}); // non-fatal — autocomplete just stays empty
  }, [isOpen]);

  const [emailError, setEmailError] = useState<string>("");
  const [dialCode,     setDialCode]     = useState("+91");
  const [altDialCode,  setAltDialCode]  = useState("+91");

  const validateEmail = (email: string): string => {
    if (!email) return ""; // Optional field, no error if empty
    
    if (!email.includes("@")) {
      return "Email must contain '@' symbol";
    }
    
    const parts = email.split("@");
    if (parts.length !== 2 || !parts[0] || !parts[1]) {
      return "Invalid email format";
    }
    
    if (!parts[1].includes(".")) {
      return "Email must contain a domain with '.' (e.g., example.com)";
    }
    
    // Check for valid email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Invalid email format";
    }
    
    return "";
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "email") {
      setEmailError(validateEmail(value));
    }

    if (name === "pincode" && value.length === 6) {
      fetch(`https://api.postalpincode.in/pincode/${value}`)
        .then((r) => r.json())
        .then((data) => {
          if (data?.[0]?.Status === "Success" && data[0].PostOffice?.length > 0) {
            const po = data[0].PostOffice[0];
            setFormData((prev) => ({
              ...prev,
              city: po.Division || po.District || prev.city,
              state: po.State || prev.state,
            }));
          }
        })
        .catch(() => {});
    }
  };

  // DOB and Age are mutually exclusive — filling one clears the other, since
  // exactly one of them may be submitted (enforced server-side too).
  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, date_of_birth: value, age_years: value ? "" : prev.age_years }));
  };

  const handleAgeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, age_years: value, date_of_birth: value ? "" : prev.date_of_birth }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.email) {
      const emailValidationError = validateEmail(formData.email);
      if (emailValidationError) {
        toast.error(emailValidationError);
        return;
      }
    }

    if (formData.aadhar_number && !/^[2-9]\d{11}$/.test(formData.aadhar_number)) {
      toast.error("Invalid Aadhaar number — must be exactly 12 digits starting with 2–9");
      return;
    }

    if (isPediatricAge && pediatricData.is_preterm && !pediatricData.gestational_age_weeks) {
      setPediatricError("Gestational age (weeks) is required for a preterm patient");
      toast.error("Gestational age (weeks) is required for a preterm patient");
      return;
    }

    const validationResult = validatePatientCreate(formData);
    if (!validationResult.success) {
      toast.error(getFirstValidationError(validationResult.error));
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    // ── Build server payload (shared by both online and offline paths) ────────
    const d = validationResult.data;
    const contactNum = d.contact_number.startsWith("+91")
      ? d.contact_number
      : `+91${d.contact_number}`;
    const altNum = d.alternate_contact_number
      ? (d.alternate_contact_number.startsWith("+91")
          ? d.alternate_contact_number
          : `+91${d.alternate_contact_number}`)
      : undefined;

    // Build address string from structured fields
    const addressParts = [d.house_no, d.building, d.street, d.locality, d.landmark].filter(Boolean);
    const addressString = addressParts.join(", ");

    // This is exactly what POST /patients expects (PatientFullCreate shape).
    // Note: no patient_id — the server assigns its own UUID.
    const serverPayload = {
      first_name:     d.first_name,
      middle_name:    d.middle_name  || undefined,
      last_name:      d.last_name,
      // Exactly one of these two is ever sent — see the DOB/Age fields above.
      ...(d.date_of_birth
        ? { date_of_birth: d.date_of_birth }
        : { age_years: Number(d.age_years) }),
      gender:         d.gender,
      contact_number: contactNum,
      contact: {
        contact_number:           contactNum,
        alternate_contact_number: altNum,
        email:                    d.email         || undefined,
        aadhar_number:            d.aadhar_number || undefined,
        abha_id:                  d.abha_id       || undefined,
      },
      address: {
        address: addressString,
        city:    d.city    || undefined,
        state:   d.state   || undefined,
        pincode: d.pincode || undefined,
      },
      tags: d.tags ?? [],
      ...(isPediatricAge && (
        pediatricData.is_preterm ||
        pediatricData.gestational_age_weeks ||
        pediatricData.birth_weight_grams ||
        pediatricData.multiple_birth ||
        pediatricData.is_down_syndrome
      ) ? {
        pediatric_profile: {
          gestational_age_weeks: pediatricData.gestational_age_weeks ? Number(pediatricData.gestational_age_weeks) : undefined,
          gestational_age_days:  pediatricData.gestational_age_days  ? Number(pediatricData.gestational_age_days)  : undefined,
          birth_weight_grams:    pediatricData.birth_weight_grams    ? Number(pediatricData.birth_weight_grams)    : undefined,
          is_preterm:            pediatricData.is_preterm,
          multiple_birth:        pediatricData.multiple_birth,
          is_down_syndrome:      pediatricData.is_down_syndrome,
        },
      } : {}),
    };

    // ── Offline helper (closure — captures all locals above) ──────────────────
    const saveOffline = async () => {
      // Read the current user's clinic_id from the local Dexie user cache so
      // the record has the correct indexed clinic_id even before it syncs.
      let clinicId: string | null = null;
      try {
        const cachedUser = await db.users.toCollection().first();
        clinicId = cachedUser?.clinic_id ?? null;
      } catch { /* non-fatal — clinic_id is optional on PatientRecord */ }

      const newPatientId = crypto.randomUUID();

      await putAndQueue<PatientRecord>(
        "patient",
        newPatientId,
        {
          patient_id:               newPatientId,
          clinic_id:                clinicId,
          is_active:                true,
          first_name:               d.first_name,
          middle_name:              d.middle_name              || null,
          last_name:                d.last_name,
          // Offline cache doesn't yet support age-only registration (no DOB) —
          // falls back to empty string; the queued sync payload below still
          // carries age_years through correctly once back online.
          date_of_birth:            d.date_of_birth ?? "",
          gender:                   d.gender as "Male" | "Female" | "Other",
          contact_number:           contactNum,
          // Extended contact/address fields — encrypted at rest via PatientRecord;
          // written here so PatientPage can show full data while still offline.
          email:                    d.email           || null,
          aadhar_number:            d.aadhar_number   || null,
          abha_id:                  d.abha_id         || null,
          alternate_contact_number: altNum            ?? null,
          address:                  addressString,
          city:                     d.city            || null,
          state:                    d.state           || null,
          pincode:                  d.pincode         || null,
        },
        "create",
        1,
        // patient_id MUST be in the sync payload so the SW batch push can create
        // the patient with the client-assigned UUID — visits reference this same UUID.
        { patient_id: newPatientId, ...(serverPayload as Record<string, unknown>) },
      );
    };

    // ── Proactive offline check: skip the fetch entirely if already offline ───
    if (!navigator.onLine) {
      try {
        await saveOffline();
        toast.success(`Patient ${d.first_name} ${d.last_name} saved offline — will sync when connected`);
        setFormData(initialFormData); setPediatricData(initialPediatricData);
        onClose();
        if (onSuccess) onSuccess();
      } catch {
        toast.error("Failed to save offline. Please try again.");
      }
      setIsSubmitting(false);
      return;
    }

    // ── Online path ───────────────────────────────────────────────────────────
    try {
      const createdPatient = await PatientAPI.create(serverPayload as any);
      toast.success(`Patient ${createdPatient?.first_name ?? d.first_name} ${createdPatient?.last_name ?? d.last_name} created successfully!`);
      setFormData(initialFormData); setPediatricData(initialPediatricData);
      onClose();
      window.dispatchEvent(new CustomEvent("patientCreated", { detail: createdPatient }));
      if (onSuccess) onSuccess(createdPatient);
    } catch (error) {
      // Refused, not offline: queueing it would report "saved offline" for a
      // patient the server has already said this user may not register.
      if (isPermissionDenied(error)) {
        toast.error(actionErrorMessage(error, "Failed to create patient"));
        setIsSubmitting(false);
        return;
      }
      // Connection dropped mid-request OR backend down — fall back to offline queue.
      if (isBackendUnreachable(error)) {
        try {
          await saveOffline();
          toast.success(`Patient ${d.first_name} ${d.last_name} saved offline — will sync when connected`);
          setFormData(initialFormData); setPediatricData(initialPediatricData);
          onClose();
          if (onSuccess) onSuccess();
        } catch {
          toast.error("Failed to save offline. Please try again.");
          setIsSubmitting(false);
        }
      } else {
        logger.error("Error creating patient", error instanceof Error ? error : undefined);
        if (error instanceof PatientApiError) {
          toast.error(error.message);
        } else if (error instanceof Error) {
          toast.error(`Failed to create patient: ${error.message}`);
        } else {
          toast.error("An unexpected error occurred. Please try again.");
        }
        setIsSubmitting(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
      />

      {/* Modal */}
      <div
        className="relative w-full md:w-auto md:min-w-[480px] md:max-w-lg max-h-[90vh] bg-white rounded-t-3xl md:rounded-2xl shadow-xl flex flex-col animate-slide-up md:animate-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-5 pt-5 pb-3 md:px-6 md:pt-6 md:pb-4 border-b border-slate-100">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg md:text-xl font-semibold text-slate-800">
                Add New Patient
              </h2>
              <p className="text-xs md:text-sm text-slate-500 mt-0.5">
                Register a new patient in the system
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Form Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-4 md:px-6 md:py-5">
          <form id="patient-form" onSubmit={handleSubmit} className="space-y-4">
            {/* First Name, Middle Name, Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  First Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>
                  <input
                    type="text"
                    name="first_name"
                    placeholder="First name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-200 bg-white text-base text-slate-700 placeholder-slate-400 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Middle Name <span className="text-slate-400 font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="middle_name"
                  placeholder="Middle name"
                  value={formData.middle_name}
                  onChange={handleChange}
                  className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base text-slate-700 placeholder-slate-400 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  placeholder="Last name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base text-slate-700 placeholder-slate-400 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors"
                />
              </div>
            </div>

            {/* Date of Birth, Age, and Gender - exactly one of DOB/Age is required */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Date of Birth {!formData.age_years && <span className="text-red-500">*</span>}
                </label>
                <DateInput
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleDobChange}
                  max={new Date().toISOString().split('T')[0]}
                  disabled={Boolean(formData.age_years)}
                  required={!formData.age_years}
                  className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base text-slate-700 placeholder-slate-400 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors disabled:bg-slate-50 disabled:text-slate-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Age (years) {!formData.date_of_birth && <span className="text-red-500">*</span>}
                </label>
                {formData.date_of_birth ? (
                  <input
                    type="text"
                    value={getPatientAge(formData.date_of_birth, undefined)}
                    disabled
                    readOnly
                    title="Calculated automatically from Date of Birth"
                    className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-slate-50 text-base text-slate-400 outline-none"
                  />
                ) : (
                  <input
                    type="number"
                    name="age_years"
                    min={0}
                    max={130}
                    value={formData.age_years ?? ""}
                    onChange={handleAgeChange}
                    placeholder="e.g. 4"
                    required={!formData.date_of_birth}
                    className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base text-slate-700 placeholder-slate-400 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors"
                  />
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Gender <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                    className="w-full h-11 px-4 pr-10 rounded-xl border border-slate-200 bg-white text-base text-slate-700 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors appearance-none"
                  >
                    <option value="" disabled>Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m6 9 6 6 6-6"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div>
              <SpecializationTagInput
                label="Tags"
                value={formData.tags ?? []}
                onChange={(tags) => setFormData((prev) => ({ ...prev, tags }))}
                placeholder="e.g. Diabetic, Follow-up"
                suggestions={tagSuggestions}
              />
            </div>

            {/* Neonatal information - pediatric patients only (age < 5y at registration).
                One-time inputs; drives WHO/Fenton growth-chart selection, never re-entered. */}
            {isPediatricAge && (
              <div className="rounded-xl border border-cyan-100 bg-cyan-50/40 p-3 space-y-3">
                <p className="text-xs font-medium text-slate-600">
                  Neonatal information (for growth-chart tracking)
                </p>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    name="is_preterm"
                    checked={pediatricData.is_preterm}
                    onChange={handlePediatricChange}
                    className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-400"
                  />
                  Born preterm (before 37 weeks gestation)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">
                      Gestational age (weeks){pediatricData.is_preterm && <span className="text-red-500"> *</span>}
                    </label>
                    <input
                      type="number"
                      name="gestational_age_weeks"
                      min={20}
                      max={45}
                      value={pediatricData.gestational_age_weeks}
                      onChange={handlePediatricChange}
                      className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-base text-slate-700 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">GA (days)</label>
                    <input
                      type="number"
                      name="gestational_age_days"
                      min={0}
                      max={6}
                      value={pediatricData.gestational_age_days}
                      onChange={handlePediatricChange}
                      className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-base text-slate-700 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">Birth weight (g)</label>
                    <input
                      type="number"
                      name="birth_weight_grams"
                      min={200}
                      max={8000}
                      value={pediatricData.birth_weight_grams}
                      onChange={handlePediatricChange}
                      className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-base text-slate-700 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors"
                    />
                  </div>
                  <div className="flex items-end pb-2.5">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        name="multiple_birth"
                        checked={pediatricData.multiple_birth}
                        onChange={handlePediatricChange}
                        className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-400"
                      />
                      Multiple birth
                    </label>
                  </div>
                  <div className="flex items-end pb-2.5">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        name="is_down_syndrome"
                        checked={pediatricData.is_down_syndrome}
                        onChange={handlePediatricChange}
                        className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-400"
                      />
                      Down syndrome
                    </label>
                  </div>
                </div>
                {pediatricError && <p className="text-xs text-red-500">{pediatricError}</p>}
              </div>
            )}

            {/* Mobile Number and Alternate Mobile - Two columns on tablet+ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <div className="flex rounded-xl border border-slate-200 bg-white overflow-visible focus-within:border-cyan-400 focus-within:ring-1 focus-within:ring-cyan-400 transition-colors">
                  <DialSelect value={dialCode} onChange={setDialCode} />
                  <input
                    type="tel"
                    name="contact_number"
                    placeholder="9876543210"
                    value={formData.contact_number}
                    onChange={handleChange}
                    maxLength={10}
                    required
                    className="flex-1 min-w-0 h-11 px-3 text-base text-slate-700 placeholder-slate-400 outline-none bg-transparent rounded-r-xl"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Alternate Mobile
                </label>
                <div className="flex rounded-xl border border-slate-200 bg-white overflow-visible focus-within:border-cyan-400 focus-within:ring-1 focus-within:ring-cyan-400 transition-colors">
                  <DialSelect value={altDialCode} onChange={setAltDialCode} />
                  <input
                    type="tel"
                    name="alternate_contact_number"
                    placeholder="9876543210"
                    value={formData.alternate_contact_number}
                    onChange={handleChange}
                    maxLength={10}
                    className="flex-1 min-w-0 h-11 px-3 text-base text-slate-700 placeholder-slate-400 outline-none bg-transparent rounded-r-xl"
                  />
                </div>
              </div>
            </div>

            {/* Email Address */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Email Address <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="20" height="16" x="2" y="4" rx="2"/>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                  </svg>
                </div>
                <input
                  type="email"
                  name="email"
                  placeholder="email@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full h-11 pl-10 pr-4 rounded-xl border ${emailError ? 'border-red-400' : 'border-slate-200'} bg-white text-base text-slate-700 placeholder-slate-400 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors`}
                />
              </div>
              {emailError && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {emailError}
                </p>
              )}
            </div>

            {/* Aadhaar Number and ABHA ID - Two columns on tablet+ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Aadhaar Number <span className="text-slate-400 font-normal">(Optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="20" height="14" x="2" y="5" rx="2"/>
                      <line x1="2" x2="22" y1="10" y2="10"/>
                    </svg>
                  </div>
                  <input
                    type="text"
                    name="aadhar_number"
                    placeholder="123456789012"
                    value={formData.aadhar_number}
                    onChange={handleChange}
                    maxLength={12}
                    className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-200 bg-white text-base text-slate-700 placeholder-slate-400 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  ABHA ID <span className="text-slate-400 font-normal">(Optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 6v6l4 2"/>
                    </svg>
                  </div>
                  <input
                    type="text"
                    name="abha_id"
                    placeholder="ABHA ID"
                    value={formData.abha_id}
                    onChange={handleChange}
                    className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-200 bg-white text-base text-slate-700 placeholder-slate-400 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Structured Address */}
            <div className="space-y-3">
              <p className="text-xs font-medium text-slate-600">Address <span className="text-slate-400 font-normal">(Optional)</span></p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">House / Flat No.</label>
                  <input type="text" name="house_no" placeholder="e.g. 42B" value={formData.house_no} onChange={handleChange}
                    className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base text-slate-700 placeholder-slate-400 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Building / Society</label>
                  <input type="text" name="building" placeholder="e.g. Sunrise Apartments" value={formData.building} onChange={handleChange}
                    className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base text-slate-700 placeholder-slate-400 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Street</label>
                  <input type="text" name="street" placeholder="e.g. MG Road" value={formData.street} onChange={handleChange}
                    className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base text-slate-700 placeholder-slate-400 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Locality</label>
                  <input type="text" name="locality" placeholder="e.g. Koramangala" value={formData.locality} onChange={handleChange}
                    className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base text-slate-700 placeholder-slate-400 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">Landmark</label>
                  <input type="text" name="landmark" placeholder="e.g. Near City Mall" value={formData.landmark} onChange={handleChange}
                    className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base text-slate-700 placeholder-slate-400 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">City</label>
                  <input type="text" name="city" placeholder="City" value={formData.city} onChange={handleChange}
                    className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base text-slate-700 placeholder-slate-400 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">State</label>
                  <div className="relative">
                    <select name="state" value={formData.state} onChange={handleChange}
                      className="w-full h-11 px-4 pr-10 rounded-xl border border-slate-200 bg-white text-base text-slate-700 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors appearance-none">
                      <option value="">Select State</option>
                      {INDIAN_STATES.map((state) => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Pincode</label>
                  <input type="text" name="pincode" placeholder="Pincode" value={formData.pincode} onChange={handleChange} maxLength={6}
                    className="w-full h-11 px-4 rounded-xl border border-slate-200 bg-white text-base text-slate-700 placeholder-slate-400 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-colors" />
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer with Buttons */}
        <div className="flex-shrink-0 px-5 py-4 md:px-6 md:py-5 border-t border-slate-100 bg-slate-50/50 rounded-b-3xl md:rounded-b-2xl">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-11 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="patient-form"
              disabled={isSubmitting}
              className="flex-1 h-11 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: isSubmitting
                  ? "#94a3b8"
                  : "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
              }}
            >
              {isSubmitting ? "Creating..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AddPatientModal;
